<!-- Dedicated Manual Event Action Banner with Explanatory Text -->
<div class="manual-event-banner">
  <div class="manual-event-banner-content">
    <div class="manual-event-banner-icon">
      <i class="fi fi-rr-bolt"></i>
    </div>
    <div class="manual-event-banner-text">
      <div class="d-flex align-items-center gap-2 flex-wrap">
        <span class="manual-event-banner-title">{{ "Manual Event Dispatch" | translate }}</span>
        <span class="manual-event-banner-tag">{{ "Conversions API" | translate }}</span>
      </div>
      <p class="manual-event-banner-desc">
        {{ "Test your pixel integration or manually push offline sales, phone orders, and missed conversions directly to Meta." | translate }}
      </p>
    </div>
  </div>
  <button
    type="button"
    class="btn manual-event-trigger-btn"
    (click)="openModal(manualEventModal)"
    [disabled]="!capiPixels || capiPixels.length === 0"
    [title]="!capiPixels || capiPixels.length === 0 ? ('Requires at least one Pixel with Conversions API (CAPI) configured' | translate) : ''">
    <i class="fi fi-rr-inbox-out mr-1.5"></i>
    <span>{{ "Test / Push Event" | translate }}</span>
  </button>
</div>

<!-- Fresh, Modern Push Event Modal -->
<ng-template #manualEventModal let-modal>
  <div class="modal-header modern-modal-header align-items-center">
    <div class="d-flex align-items-center gap-3">
      <div class="modern-header-badge" [class.live-badge]="mode === 'live'">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      </div>
      <div>
        <div class="d-flex align-items-center gap-2">
          <h5 class="modern-modal-title" id="manual-event-title">
            {{ "Push Pixel Event Manually" | translate }}
          </h5>
          <span class="mode-pill" [ngClass]="mode === 'live' ? 'mode-pill-live' : 'mode-pill-test'">
            {{ (mode === 'live' ? 'LIVE MODE' : 'TEST MODE') | translate }}
          </span>
        </div>
        <span class="modern-modal-subtitle">
          {{ "Send offline, phone, or backfilled conversion events directly to Meta via CAPI." | translate }}
        </span>
      </div>
    </div>
    <button type="button" class="modern-close-btn" aria-label="Close" (click)="modal.dismiss('cross')">
      <i class="fi fi-rr-cross-small"></i>
    </button>
  </div>

  <div class="modal-body modern-modal-body p-3 p-md-4">
    <!-- Success Banner -->
    <div class="modern-alert modern-alert-success mb-3" *ngIf="submissionResult?.status === 'success'">
      <div class="modern-alert-icon">
        <i class="fi fi-rr-check"></i>
      </div>
      <div class="modern-alert-content flex-grow-1">
        <div class="modern-alert-title">{{ submissionResult?.message }}</div>
        <div class="modern-alert-meta" *ngIf="submissionResult?.traceId">
          <span>Meta Trace ID: <code>{{ submissionResult?.traceId }}</code></span> &bull;
          <span>{{ submissionResult?.timestamp }}</span>
        </div>

        <!-- Meta Guideline: Expected Reflection Timeframe Notice -->
        <div class="meta-timeframe-notice mt-2 pt-2 border-top">
          <div class="d-flex align-items-center gap-1.5 font-semibold text-xs mb-1">
            <i class="fi fi-rr-clock-three text-emerald-700"></i>
            <span>{{ "Expected Reflection Timeframe (Meta Guideline)" | translate }}</span>
          </div>
          <div class="text-xs text-emerald-900 line-height-sm" *ngIf="submissionResult?.mode === 'live'">
            <ul class="mb-0 pl-3.5 space-y-0.5">
              <li>
                <strong>{{ "Events Manager Activity:" | translate }}</strong>
                {{ "Typically reflects in recent activity and charts within 15 to 30 minutes as Meta processes and deduplicates the event." | translate }}
              </li>
              <li>
                <strong>{{ "Ads Manager Reporting:" | translate }}</strong>
                {{ "Conversion attribution and campaign ROAS metrics may take 24 to 72 hours to fully update." | translate }}
              </li>
            </ul>
            <p class="mb-0 mt-1 text-emerald-800" style="font-size: 11px;">
              <i class="fi fi-rr-info mr-0.5"></i>
              {{ "Your event was acknowledged by Meta immediately with the Trace ID above. There is no need to re-send." | translate }}
            </p>
          </div>
          <div class="text-xs text-emerald-900" *ngIf="submissionResult?.mode === 'test'">
            <span>{{ "Test events typically reflect in Meta Events Manager under the 'Test Events' tab within a few seconds to a few minutes. Please allow a brief moment to refresh." | translate }}</span>
          </div>
        </div>
      </div>
      <button type="button" class="modern-alert-close" (click)="dismissAlert()" title="{{ 'Dismiss' | translate }}" aria-label="Close">
        <i class="fi fi-rr-cross-small"></i>
      </button>
    </div>

    <!-- Error Banner -->
    <div id="manualEventErrorBanner" class="modern-alert modern-alert-error mb-3" *ngIf="submissionResult?.status === 'error'">
      <div class="modern-alert-content flex-grow-1">
        <div class="modern-alert-title">{{ "Could not deliver event" | translate }}</div>
        <div class="modern-alert-text">{{ submissionResult?.message }}</div>
      </div>
      <button type="button" class="modern-alert-close" (click)="dismissAlert()" title="{{ 'Dismiss' | translate }}" aria-label="Close">
        <i class="fi fi-rr-cross-small"></i>
      </button>
    </div>

    <!-- Warning if no pixel configured at all -->
    <div class="modern-alert modern-alert-warning mb-3" *ngIf="!allPixels || allPixels.length === 0">
      <div class="modern-alert-icon">
        <i class="fi fi-rr-exclamation"></i>
      </div>
      <div class="modern-alert-content">
        <div class="modern-alert-title">{{ "No Facebook Pixel Configured" | translate }}</div>
        <div class="modern-alert-text">
          {{ "Please add a Pixel ID first in the settings above before triggering events." | translate }}
        </div>
      </div>
    </div>

    <!-- Warning if no pixel has CAPI enabled -->
    <div class="modern-alert modern-alert-warning mb-3" *ngIf="allPixels.length > 0 && capiPixels.length === 0">
      <div class="modern-alert-icon">
        <i class="fi fi-rr-exclamation"></i>
      </div>
      <div class="modern-alert-content">
        <div class="modern-alert-title">{{ "No Conversions API (CAPI) Pixel Configured" | translate }}</div>
        <div class="modern-alert-text">
          {{ "Only pixels with a Conversions API token can receive manual events. Add a token in Tracking & Analytics settings." | translate }}
        </div>
      </div>
    </div>

    <form [formGroup]="form" (ngSubmit)="handlePrimaryAction()" *ngIf="allPixels && allPixels.length > 0">
      
      <!-- SECTION 1: Event Context & Origin -->
      <div class="modern-card mb-3">
        <div class="modern-card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div class="d-flex align-items-center gap-2">
            <span class="modern-section-tag">1</span>
            <span class="modern-card-title">{{ "Event Context & Origin" | translate }}</span>
          </div>

          <!-- Segmented Toggle: Test vs Live -->
          <div class="mode-segmented-control" role="group" aria-label="Event Mode">
            <button
              type="button"
              class="mode-toggle-btn"
              [class.active]="mode === 'test'"
              (click)="setMode('test')">
              <i class="fi fi-rr-test-tube mr-1"></i>
              {{ "Test Mode" | translate }}
            </button>
            <button
              type="button"
              class="mode-toggle-btn mode-toggle-live"
              [class.active]="mode === 'live'"
              (click)="setMode('live')">
              <span class="live-dot" *ngIf="mode === 'live'"></span>
              <i class="fi fi-rr-signal-alt mr-1" *ngIf="mode !== 'live'"></i>
              {{ "Live Mode" | translate }}
            </button>
          </div>
        </div>

        <div class="modern-card-body">
          <!-- Test Mode: Test Event Code (Required) -->
          <div class="test-code-banner mb-3" *ngIf="mode === 'test'">
            <label class="modern-field-label required">
              {{ "Test Event Code" | translate }}
            </label>
            <input
              type="text"
              class="form-control modern-input mb-1 font-monospace"
              placeholder="e.g. TEST12345"
              formControlName="test_event_code" />
            <small class="modern-field-desc">
              {{ "Find this in Meta Events Manager > Test Events. Test events don't affect your reporting or ad optimization." | translate }}
            </small>
          </div>

          <!-- Live Mode Warning Note -->
          <div class="live-warning-banner mb-3" *ngIf="mode === 'live'">
            <i class="fi fi-rr-signal-alt mr-2 text-amber-600 flex-shrink-0"></i>
            <div>
              <span>{{ "Live events count toward your ad reporting and optimization. Only send real sales." | translate }}</span>
              <small class="d-block text-amber-800 mt-0.5" style="font-size: 11px;">
                {{ "Note: Events reflect in Events Manager within 15–30 minutes and Ads Manager within 24–72 hours." | translate }}
              </small>
            </div>
          </div>

          <!-- Pixel ID Dropdown -->
          <div class="form-group mb-3">
            <label class="modern-field-label">{{ "Facebook Pixel ID" | translate }}</label>
            <select class="form-control modern-input" formControlName="pixel_id">
              <option *ngFor="let p of allPixels" [value]="p.pixel_id" [disabled]="!isCapiPixel(p)">
                {{ getPixelDisplayLabel(p) }}
              </option>
            </select>
            <small class="modern-field-desc mt-1 d-block text-secondary" *ngIf="hasNonCapiPixel">
              {{ "Only pixels with a Conversions API token can receive manual events. Add a token in Tracking & Analytics settings." | translate }}
            </small>
          </div>

          <!-- Where did this sale happen? (4 Cards) -->
          <div class="form-group mb-3" id="actionSourceGrid">
            <label class="modern-field-label">
              {{ "Where did this sale happen?" | translate }}
            </label>
            <p class="modern-field-desc mb-2">
              {{ "Choose where the sale happened. Meta requires this to be accurate." | translate }}
            </p>

            <div class="action-source-grid">
              <div
                *ngFor="let src of actionSources"
                class="action-source-tile"
                [class.active]="form.get('action_source')?.value === src.value"
                (click)="selectActionSource(src.value)">
                <!-- Checkmark in top-right corner when selected -->
                <div class="tile-check" *ngIf="form.get('action_source')?.value === src.value">
                  <i class="fi fi-rr-check"></i>
                </div>

                <!-- Technical Info Tooltip Icon in Bottom-Right Corner on ALL cards -->
                <i
                  class="fi fi-rr-info card-info-tooltip-icon"
                  placement="top"
                  container="body"
                  [ngbTooltip]="'Sent to Meta as action_source: ' + src.value"
                  [title]="'Sent to Meta as action_source: ' + src.value"
                  (click)="$event.stopPropagation()"></i>

                <div class="tile-icon-wrap">
                  <i class="fi" [ngClass]="src.icon"></i>
                </div>
                <div class="tile-info">
                  <span class="tile-title">{{ src.title | translate }}</span>
                  <span class="tile-subtitle">{{ src.subtitle | translate }}</span>
                </div>
              </div>
            </div>

            <!-- Error when sale location is missing in Live mode -->
            <div class="text-danger text-xs mt-2 font-medium d-flex align-items-center" *ngIf="showActionSourceError">
              <i class="fi fi-rr-exclamation mr-1"></i>
              {{ "Choose where this sale happened." | translate }}
            </div>
          </div>

          <!-- Event Date & Time -->
          <div class="form-group mb-0">
            <div class="d-flex justify-content-between align-items-start mb-2 flex-wrap gap-2">
              <div>
                <label class="modern-field-label mb-0" for="eventTimeBox">
                  {{ "Event Date & Time" | translate }}
                </label>
                <span class="modern-field-desc d-block mt-0.5">
                  {{ "Backfilling requires sending the time the event actually occurred." | translate }}
                </span>
              </div>
              <div class="d-flex align-items-center gap-2 flex-wrap pt-0.5">
                <span class="modern-badge modern-badge-info" *ngIf="form.get('action_source')?.value">
                  {{ "Allowed: Last " + currentActionSourceMaxDays + " days" | translate }}
                </span>
                <button
                  type="button"
                  class="btn btn-link btn-sm p-0 modern-link-btn"
                  (click)="setEventTimeToNow()">
                  <i class="fi fi-rr-clock mr-1"></i>
                  {{ "Set to Current Time" | translate }}
                </button>
              </div>
            </div>

            <!-- Single Interactive Formatted Date & Time Box with Relative Wrapper -->
            <div class="datetime-interactive-wrapper position-relative">
              <div
                id="eventTimeBox"
                class="datetime-interactive-box modern-input d-flex align-items-center justify-content-between gap-2 p-2.5 rounded cursor-pointer"
                [class.is-invalid]="!!dateError"
                tabindex="0"
                (click)="openDateTimePicker(dateTimeInput)"
                (keydown.enter)="openDateTimePicker(dateTimeInput); $event.preventDefault()"
                (keydown.space)="openDateTimePicker(dateTimeInput); $event.preventDefault()">
                <div class="d-flex align-items-center gap-2 min-w-0">
                  <i class="fi fi-rr-calendar text-primary text-base flex-shrink-0"></i>
                  <span class="text-sm font-semibold text-dark text-truncate">
                    {{ getFormattedEventDateTime() || ("Select a date & time" | translate) }}
                  </span>
                </div>
                <div class="d-flex align-items-center gap-2 flex-shrink-0">
                  <span class="badge badge-light border text-secondary text-xs font-medium">
                    <i class="fi fi-rr-globe mr-1 text-primary"></i>{{ getTimezoneDisplay() }}
                  </span>
                  <i class="fi fi-rr-angle-small-down text-muted text-sm"></i>
                </div>
              </div>

              <!-- Native datetime-local anchored to the exact box coordinates for popup positioning -->
              <input
                #dateTimeInput
                type="datetime-local"
                class="datetime-hidden-native-input"
                [min]="minEventDateTime"
                [max]="maxEventDateTime"
                formControlName="event_time" />
            </div>

            <div class="invalid-feedback d-block mt-1" *ngIf="dateError">
              <i class="fi fi-rr-exclamation mr-1"></i>{{ dateError }}
            </div>
          </div>
        </div>
      </div>

      <!-- SECTION 2: Conversion & Value -->
      <div class="modern-card mb-3">
        <div class="modern-card-header d-flex align-items-center gap-2">
          <span class="modern-section-tag">2</span>
          <span class="modern-card-title">{{ "Conversion & Value" | translate }}</span>
        </div>

        <div class="modern-card-body">

          <!-- Storefront Order Picker (When Storefront is selected) -->
          <div class="form-group mb-3" id="storefrontOrderPickerBox" *ngIf="form.get('action_source')?.value === 'website'">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <label class="modern-field-label mb-0" [class.required]="mode === 'live'">
                {{ "Storefront Order" | translate }}
                <span *ngIf="mode === 'test'" class="text-secondary font-normal text-xs ml-1">({{ "optional in Test mode" | translate }})</span>
              </label>
              <button
                *ngIf="selectedStorefrontOrder"
                type="button"
                class="btn btn-link btn-sm p-0 modern-link-btn"
                (click)="clearSelectedOrder()">
                <i class="fi fi-rr-refresh mr-1"></i>
                {{ "Change order" | translate }}
              </button>
            </div>
            <p class="modern-field-desc mb-2" *ngIf="isLiveStorefrontAwaitingOrder">
              {{ "Select an order to load its details." | translate }}
            </p>
            <p class="modern-field-desc mb-2" *ngIf="!isLiveStorefrontAwaitingOrder">
              {{ "Select an order from the last 7 days to prefill transaction, customer, and product details." | translate }}
            </p>

            <!-- If NO order selected: Searchable Dropdown Picker -->
            <div *ngIf="!selectedStorefrontOrder" class="order-picker-wrapper position-relative" [class.dropdown-active]="isOrderDropdownOpen">
              <div
                class="order-picker-trigger modern-input d-flex align-items-center justify-content-between cursor-pointer"
                [class.is-invalid]="showStorefrontOrderError"
                tabindex="0"
                (click)="toggleOrderDropdown()"
                (keydown.enter)="toggleOrderDropdown()"
                (keydown.space)="toggleOrderDropdown()">
                <div class="d-flex align-items-center gap-2 text-truncate">
                  <i class="fi fi-rr-shopping-bag text-primary flex-shrink-0"></i>
                  <span class="text-sm text-dark font-medium" *ngIf="isLoadingOrders">
                    <i class="fa fa-spinner fa-spin mr-1"></i>{{ "Loading recent orders..." | translate }}
                  </span>
                  <span class="text-sm text-secondary" *ngIf="!isLoadingOrders">
                    {{ "Search or select a storefront order..." | translate }}
                  </span>
                </div>
                <i class="fi fi-rr-angle-small-down text-muted chevron-icon" [class.chevron-open]="isOrderDropdownOpen"></i>
              </div>

              <!-- Dropdown Menu -->
              <div class="order-picker-menu" *ngIf="isOrderDropdownOpen">
                <div class="order-search-box p-2 border-bottom">
                  <div class="position-relative">
                    <i class="fi fi-rr-search order-search-icon"></i>
                    <input
                      type="text"
                      class="form-control form-control-sm order-search-input pl-4"
                      placeholder="{{ 'Search by order #, customer name, email...' | translate }}"
                      [(ngModel)]="orderSearchQuery"
                      [ngModelOptions]="{ standalone: true }"
                      (click)="$event.stopPropagation()"
                      autofocus />
                  </div>
                </div>

                <div class="order-options-list">
                  <div *ngIf="isLoadingOrders" class="p-3 text-center text-muted text-xs">
                    <i class="fa fa-spinner fa-spin mr-1"></i>{{ "Fetching storefront orders..." | translate }}
                  </div>
                  <div *ngIf="!isLoadingOrders && ordersLoadError" class="p-3 text-center text-danger text-xs">
                    {{ ordersLoadError }}
                  </div>
                  <div *ngIf="!isLoadingOrders && !ordersLoadError && filteredStorefrontOrders.length === 0" class="p-3 text-center text-muted text-xs">
                    {{ "No orders found from the last 7 days." | translate }}
                  </div>

                  <div
                    *ngFor="let ord of filteredStorefrontOrders"
                    class="order-option-item p-2 border-bottom d-flex align-items-center justify-content-between gap-2"
                    [class.order-option-disabled]="mode === 'live' && !ord.is_selectable_live"
                    [title]="mode === 'live' && !ord.is_selectable_live ? ord.tooltip : ''"
                    (click)="selectStorefrontOrder(ord)">
                    <div class="order-option-info min-w-0">
                      <div class="d-flex align-items-center gap-1.5 text-truncate flex-wrap">
                        <strong class="text-xs text-dark">#{{ ord.store_order_id || ord.order_id }}</strong>
                        <span class="badge badge-light border text-muted px-1 py-0.5" style="font-size: 10px;" *ngIf="ord.fiv5s_order_id">5S: #{{ ord.fiv5s_order_id }}</span>
                        <span class="text-muted text-xs">&bull;</span>
                        <span class="text-xs text-secondary">{{ formatOrderDate(ord.created_at) }}</span>
                        <span class="text-muted text-xs">&bull;</span>
                        <span class="text-xs text-dark font-medium text-truncate">{{ ord.customer_name || 'Guest' }}</span>
                        <span class="text-muted text-xs">&bull;</span>
                        <span class="text-xs text-success font-semibold">{{ ord.currency }} {{ ord.total_display || (ord.total_amount | number:'1.2-2') }}</span>
                      </div>
                      <div class="text-xs text-muted text-truncate" *ngIf="ord.customer_email">
                        {{ ord.customer_email }}
                      </div>
                      <div class="text-xs text-warning text-truncate" *ngIf="mode === 'live' && !ord.is_selectable_live && ord.tooltip">
                        <i class="fi fi-rr-lock mr-1"></i>{{ ord.tooltip }}
                      </div>
                    </div>
                    <div class="order-option-badge flex-shrink-0">
                      <span class="order-status-badge" [ngClass]="'badge-' + ord.tracking_status">
                        {{ ord.tracking_label }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- If order IS selected: Show Rich Order Summary Card -->
            <div *ngIf="selectedStorefrontOrder" class="selected-order-card p-2.5 rounded border bg-light d-flex align-items-center justify-content-between gap-2">
              <div class="d-flex align-items-center gap-2.5 min-w-0">
                <div class="order-card-icon">
                  <i class="fi fi-rr-shopping-bag text-primary"></i>
                </div>
                <div class="order-card-details min-w-0">
                  <div class="d-flex align-items-center gap-2 flex-wrap">
                    <span class="order-card-no font-semibold text-sm text-dark">Order #{{ selectedStorefrontOrder.store_order_id || selectedStorefrontOrder.order_id }}</span>
                    <span class="badge badge-light border text-muted px-1.5 py-0.5" style="font-size: 11px;" *ngIf="selectedStorefrontOrder.fiv5s_order_id">5S: #{{ selectedStorefrontOrder.fiv5s_order_id }}</span>
                    <span class="order-status-badge" [ngClass]="'badge-' + selectedStorefrontOrder.tracking_status">
                      {{ selectedStorefrontOrder.tracking_label }}
                    </span>
                  </div>
                  <div class="order-card-meta text-xs text-secondary mt-0.5 text-truncate">
                    <span>{{ formatOrderDate(selectedStorefrontOrder.created_at) }}</span> &bull;
                    <span>{{ selectedStorefrontOrder.customer_name || 'Guest' }}</span>
                    <span *ngIf="selectedStorefrontOrder.customer_email"> ({{ selectedStorefrontOrder.customer_email }})</span> &bull;
                    <strong class="text-success">{{ selectedStorefrontOrder.currency }} {{ selectedStorefrontOrder.total_display || (selectedStorefrontOrder.total_amount | number:'1.2-2') }}</strong>
                  </div>
                </div>
              </div>
              <button type="button" class="btn btn-sm btn-outline-secondary order-change-btn flex-shrink-0" (click)="clearSelectedOrder()">
                <i class="fi fi-rr-pencil mr-1"></i>{{ "Change" | translate }}
              </button>
            </div>

            <!-- Inline Error for Live Mode when no order selected -->
            <div class="text-danger text-xs mt-1.5 font-medium d-flex align-items-center" *ngIf="showStorefrontOrderError">
              <i class="fi fi-rr-exclamation mr-1"></i>
              {{ "Select the order you want to send." | translate }}
            </div>
          </div>

          <!-- Fields Below Order Picker in Section 2 -->
          <div>
            <!-- Event Name -->
            <div class="form-group mb-3">
              <label class="modern-field-label">{{ "Event Name" | translate }}</label>
              <select class="form-control modern-input mb-1.5" formControlName="event_select">
                <option *ngFor="let ev of availableEventsList; trackBy: trackByEventValue" [value]="ev.value">
                  {{ ev.label }}
                </option>
              </select>

              <div *ngIf="form.get('event_select')?.value === 'CUSTOM'" class="mt-2">
                <input type="text" class="form-control modern-input" placeholder="e.g. WhatsAppLead, InStoreOrder" formControlName="custom_event_name" />
              </div>
            </div>

            <!-- Value, Currency & Order ID in a responsive row -->
            <div class="row g-2">
              <div [ngClass]="form.get('action_source')?.value === 'website' ? 'col-12 col-sm-6' : 'col-12 col-sm-4'">
                <label class="modern-field-label">
                  {{ "Currency" | translate }}
                </label>
                <select
                  class="form-control modern-input"
                  formControlName="currency">
                  <option *ngFor="let c of currencies" [value]="c">{{ c }}</option>
                </select>
              </div>

              <div [ngClass]="form.get('action_source')?.value === 'website' ? 'col-12 col-sm-6' : 'col-12 col-sm-4'">
                <label class="modern-field-label" for="conversionAmountInput" [class.required]="isConversionAmountRequired">
                  {{ "Conversion Amount" | translate }}
                  <span *ngIf="!isConversionAmountRequired" class="text-secondary font-normal text-xs ml-1">({{ "optional" | translate }})</span>
                </label>
                <input
                  id="conversionAmountInput"
                  type="number"
                  step="0.01"
                  min="0"
                  class="form-control modern-input"
                  [class.is-invalid]="showAmountError"
                  placeholder="0.00"
                  formControlName="value"
                  (blur)="onAmountBlur()" />
                <div class="invalid-feedback d-block mt-1" *ngIf="showAmountError">
                  <i class="fi fi-rr-exclamation mr-1"></i>
                  {{ "Enter the sale amount." | translate }}
                </div>
              </div>

              <!-- Hide Order ID input when action_source is website -->
              <div class="col-12 col-sm-4" *ngIf="form.get('action_source')?.value !== 'website'">
                <label class="modern-field-label">
                  {{ "Order / Reference No." | translate }}
                  <span class="text-secondary font-normal text-xs ml-1">({{ "optional" | translate }})</span>
                </label>
                <input type="text" class="form-control modern-input" placeholder="e.g. ORD-10293, INV-8821" formControlName="order_id" />
                <small class="modern-field-desc mt-1 d-block">
                  {{ (mode === 'live'
                      ? "Your receipt, invoice or order number. Used to prevent the same sale being counted twice. Leave empty and we'll generate one."
                      : "Your receipt, invoice or order number. Used to prevent the same sale being counted twice.") | translate }}
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- SECTION 3: Customer Details (Always Expanded) -->
      <div class="modern-card mb-3">
        <div class="modern-card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div class="d-flex align-items-center gap-2">
            <span class="modern-section-tag">3</span>
            <span class="modern-card-title">{{ "Customer Details" | translate }}</span>
          </div>

          <!-- Match Strength Indicator -->
          <div class="match-strength-indicator" [ngClass]="'strength-' + matchStrength">
            <span class="strength-dot"></span>
            <span class="strength-label">
              <ng-container [ngSwitch]="matchStrength">
                <span *ngSwitchCase="'strong'">{{ "Strong Match" | translate }}</span>
                <span *ngSwitchCase="'good'">{{ "Good Match" | translate }}</span>
                <span *ngSwitchCase="'weak'">{{ "Weak Match" | translate }}</span>
                <span *ngSwitchDefault>{{ "No match data" | translate }}</span>
              </ng-container>
            </span>
          </div>
        </div>

        <div class="modern-card-body">
          <p class="modern-field-desc mb-3">
            {{ "Providing customer details helps Meta match this conversion to your ad campaigns." | translate }}
            <span class="text-muted">{{ "Customer data is hashed with SHA-256 before it is sent to Meta." | translate }}</span>
          </p>

          <!-- Group Label for Email / Phone (required in both Test and Live mode) -->
          <div class="mb-2">
            <label class="modern-field-label required mb-0">
              {{ "Email or phone (one required)" | translate }}
            </label>
            <p class="modern-field-desc">
              {{ "Meta requires at least one customer contact identifier to match conversions to ad clicks." | translate }}
            </p>
          </div>

          <!-- Email & Phone in a responsive row -->
          <div class="row g-2 mb-2">
            <div class="col-12 col-sm-6">
              <label class="modern-field-label" for="customerEmailInput">
                {{ "Email" | translate }}
              </label>
              <input
                id="customerEmailInput"
                type="email"
                class="form-control modern-input"
                [class.is-invalid]="showEmailFormatError"
                placeholder="customer@example.com"
                formControlName="customer_email"
                (blur)="onEmailBlur()" />
              <div class="invalid-feedback d-block mt-1" *ngIf="showEmailFormatError">
                <i class="fi fi-rr-exclamation mr-1"></i>
                {{ "Enter a valid email address." | translate }}
              </div>
            </div>
            <div class="col-12 col-sm-6">
              <label class="modern-field-label" for="customerPhoneNumberInput">
                {{ "Phone" | translate }}
              </label>
              <div class="phone-split-input-group">
                <!-- Custom Phone Country Code Dropdown -->
                <div class="custom-phone-select-wrapper" [class.dropdown-active]="isPhoneDropdownOpen">
                  <div
                    class="custom-phone-trigger modern-input"
                    tabindex="0"
                    (click)="togglePhoneDropdown()"
                    (keydown.enter)="togglePhoneDropdown()"
                    (keydown.space)="togglePhoneDropdown()"
                  >
                    <span class="phone-trigger-content">
                      <img class="flag-icon" [src]="getFlagUrl(getSelectedPhoneCountry().code)" width="16" height="12" alt="flag" />
                      <span class="phone-code-text">{{ getSelectedPhoneCountry().phoneCode }}</span>
                    </span>
                    <i class="fi fi-rr-angle-small-down phone-chevron" [class.chevron-open]="isPhoneDropdownOpen"></i>
                  </div>

                  <!-- Searchable Popover Menu for Phone Code -->
                  <div class="custom-phone-menu" *ngIf="isPhoneDropdownOpen">
                    <div class="phone-search-box">
                      <i class="fi fi-rr-search phone-search-icon"></i>
                      <input
                        type="text"
                        class="phone-search-input"
                        placeholder="{{ 'Search country or code...' | translate }}"
                        [(ngModel)]="phoneSearchQuery"
                        [ngModelOptions]="{ standalone: true }"
                        (click)="$event.stopPropagation()"
                        autofocus
                      />
                    </div>
                    <div class="phone-options-list">
                      <div
                        *ngFor="let c of filteredPhoneCountries"
                        class="phone-option-item"
                        [class.option-selected]="form.get('customer_phone_prefix')?.value === c.phoneCode"
                        (click)="selectPhoneCountry(c, $event)"
                      >
                        <span class="phone-option-left">
                          <img class="flag-icon" [src]="getFlagUrl(c.code)" width="16" height="12" alt="flag" />
                          <span class="phone-country-name">{{ c.name }}</span>
                        </span>
                        <span class="phone-code-badge">{{ c.phoneCode }}</span>
                      </div>
                      <div *ngIf="filteredPhoneCountries.length === 0" class="phone-no-results text-center py-2 text-secondary text-xs">
                        {{ "No matches found" | translate }}
                      </div>
                    </div>
                  </div>
                </div>

                <input
                  id="customerPhoneNumberInput"
                  type="tel"
                  class="form-control modern-input phone-number-input"
                  placeholder="12 345 6789"
                  formControlName="customer_phone_number"
                  (blur)="onPhoneBlur()" />
              </div>
            </div>
          </div>

          <!-- Missing Contact Warning (only when triggered by blur or submit attempt) -->
          <div class="text-danger text-xs mb-3 font-medium d-flex align-items-center" *ngIf="showContactError">
            <i class="fi fi-rr-info mr-1"></i>
            {{ "Add an email or phone number so Meta can match this sale to your ads." | translate }}
          </div>

          <!-- Name & Customer ID -->
          <div class="row g-2 mb-2.5">
            <div class="col-12 col-sm-4">
              <label class="modern-field-label">
                <span>{{ "First Name" | translate }}</span>
              </label>
              <input
                type="text"
                class="form-control modern-input"
                placeholder="John"
                formControlName="first_name" />
            </div>
            <div class="col-12 col-sm-4">
              <label class="modern-field-label">
                <span>{{ "Last Name" | translate }}</span>
              </label>
              <input
                type="text"
                class="form-control modern-input"
                placeholder="Doe"
                formControlName="last_name" />
            </div>
            <div class="col-12 col-sm-4">
              <label class="modern-field-label">
                <span>{{ "Customer ID" | translate }}</span>
                <i
                  class="fi fi-rr-info customer-id-info-icon"
                  placement="top"
                  container="body"
                  [ngbTooltip]="'Your own customer or member ID. Sent to Meta as external_id to improve matching.' | translate"
                  [title]="'Your own customer or member ID. Sent to Meta as external_id to improve matching.' | translate"
                  (click)="$event.stopPropagation()"></i>
              </label>
              <input
                type="text"
                class="form-control modern-input"
                placeholder="e.g. CUST-882"
                formControlName="customer_id" />
            </div>
          </div>

          <!-- Location Fields: City, Postcode, Country -->
          <div class="row g-2 mb-3">
            <div class="col-12 col-sm-4">
              <label class="modern-field-label">
                <span>{{ "City" | translate }}</span>
              </label>
              <input
                type="text"
                class="form-control modern-input"
                placeholder="Kuala Lumpur"
                formControlName="city" />
            </div>
            <div class="col-12 col-sm-4">
              <label class="modern-field-label">
                <span>{{ "Postcode" | translate }}</span>
              </label>
              <input
                type="text"
                class="form-control modern-input"
                placeholder="50000"
                formControlName="postcode" />
            </div>
            <div class="col-12 col-sm-4">
              <label class="modern-field-label">
                <span>{{ "Country" | translate }}</span>
              </label>
              <div class="custom-country-select-wrapper" [class.dropdown-active]="isCountryDropdownOpen">
                <div
                  class="custom-country-trigger modern-input"
                  tabindex="0"
                  (click)="toggleCountryDropdown()"
                  (keydown.enter)="toggleCountryDropdown()"
                  (keydown.space)="toggleCountryDropdown()"
                >
                  <span class="country-trigger-content">
                    <ng-container *ngIf="getSelectedCountry() as selectedCountry; else noCountrySelected">
                      <img class="flag-icon" [src]="getFlagUrl(selectedCountry.code)" width="16" height="12" alt="flag" />
                      <span class="country-name-text">{{ selectedCountry.name }}</span>
                    </ng-container>
                    <ng-template #noCountrySelected>
                      <span class="country-name-text text-secondary">{{ "Select country" | translate }}</span>
                    </ng-template>
                  </span>
                  <i class="fi fi-rr-angle-small-down country-chevron" [class.chevron-open]="isCountryDropdownOpen"></i>
                </div>

                <!-- Searchable Popover Menu -->
                <div class="custom-country-menu" *ngIf="isCountryDropdownOpen">
                  <div class="country-search-box">
                    <i class="fi fi-rr-search country-search-icon"></i>
                    <input
                      type="text"
                      class="country-search-input"
                      placeholder="{{ 'Search country...' | translate }}"
                      [(ngModel)]="countrySearchQuery"
                      [ngModelOptions]="{ standalone: true }"
                      (click)="$event.stopPropagation()"
                      autofocus
                    />
                  </div>
                  <div class="country-options-list">
                    <div
                      *ngIf="form.get('country')?.value && !countrySearchQuery"
                      class="country-option-item"
                      (click)="clearCountry($event)"
                    >
                      <span class="country-option-left">
                        <span class="country-name text-secondary">{{ "Not specified" | translate }}</span>
                      </span>
                    </div>
                    <div
                      *ngFor="let c of filteredCountries"
                      class="country-option-item"
                      [class.option-selected]="form.get('country')?.value === c.code"
                      (click)="selectCountry(c, $event)"
                    >
                      <span class="country-option-left">
                        <img class="flag-icon" [src]="getFlagUrl(c.code)" width="16" height="12" alt="flag" />
                        <span class="country-name">{{ c.name }}</span>
                      </span>
                      <i *ngIf="form.get('country')?.value === c.code" class="fi fi-rr-check text-primary text-xs ml-1.5"></i>
                    </div>
                    <div *ngIf="filteredCountries.length === 0" class="country-no-results text-center py-2 text-secondary text-xs">
                      {{ "No countries match" | translate }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Customer Consent Checkbox -->
          <div
            class="consent-checkbox-wrap"
            [class.consent-required]="mode === 'live'"
            [class.consent-checked]="form.get('customer_consent')?.value"
            [class.consent-has-error]="showConsentError"
            (click)="toggleConsent($event)"
          >
            <label class="consent-label" for="manualCustomerConsentCheck">
              <input
                id="manualCustomerConsentCheck"
                type="checkbox"
                class="consent-checkbox-input"
                formControlName="customer_consent"
                (change)="onConsentChange()"
                (blur)="onConsentBlur()"
                (click)="$event.stopPropagation()"
              />
              <span class="consent-text">
                {{ "I have the customer's consent to share their contact details with Meta for ad measurement." | translate }}
                <strong *ngIf="mode === 'live'" class="text-danger" title="{{ 'Required in Live Mode' | translate }}">*</strong>
              </span>
            </label>

            <!-- Inline Error for Live Mode -->
            <div
              *ngIf="showConsentError"
              class="consent-inline-error"
            >
              <i class="fi fi-rr-exclamation mr-1"></i>
              {{ "Please confirm you have the customer's consent." | translate }}
            </div>
          </div>
        </div>
      </div>

      <!-- SECTION 4: Products (optional) -->
      <div class="modern-card mb-3">
        <div
          class="modern-card-header d-flex justify-content-between align-items-center cursor-pointer"
          (click)="toggleProductsCollapse()">
          <div class="d-flex align-items-center gap-2 min-w-0">
            <span class="modern-section-tag">4</span>
            <span class="modern-card-title text-truncate">{{ "Products (optional)" | translate }}</span>
          </div>
          <div class="d-flex align-items-center gap-2 flex-shrink-0">
            <span class="products-summary-line text-xs text-muted" *ngIf="isProductsCollapsed">
              {{ productsSummaryText }}
            </span>
            <button type="button" class="btn btn-link btn-sm text-secondary p-0" aria-label="Toggle Products Section">
              <i class="fi" [ngClass]="isProductsCollapsed ? 'fi-rr-angle-small-down' : 'fi-rr-angle-small-up'"></i>
            </button>
          </div>
        </div>

        <div class="modern-card-body" *ngIf="!isProductsCollapsed">
          <p class="modern-field-desc mb-3">
            {{ "Specify individual line items from this sale. Meta uses product SKUs to match catalog items and power product-level ad reporting." | translate }}
          </p>

          <div *ngIf="contents.length === 0" class="empty-products-placeholder text-center p-3 border border-dashed rounded mb-2">
            <span class="text-secondary text-sm">{{ "No line items added yet." | translate }}</span>
            <div class="mt-2">
              <button type="button" class="btn btn-sm btn-outline-primary" (click)="addContentItem()">
                <i class="fi fi-rr-plus mr-1"></i>{{ "Add Line Item" | translate }}
              </button>
            </div>
          </div>

          <!-- Line Items Table -->
          <div *ngIf="contents.length > 0" class="line-items-table-container">
            <div class="line-items-header-row d-none d-md-flex align-items-center gap-2 mb-1.5 px-2 text-xs font-semibold text-secondary">
              <div style="flex: 2;">{{ "Product SKU" | translate }}</div>
              <div style="flex: 2;">{{ "Product Name (optional)" | translate }}</div>
              <div style="width: 85px;">{{ "Quantity" | translate }}</div>
              <div style="width: 125px;">{{ "Unit Price" | translate }} ({{ form.get('currency')?.value || 'MYR' }})</div>
              <div style="width: 36px;"></div>
            </div>

            <div formArrayName="contents">
              <div
                *ngFor="let item of contents.controls; let idx = index"
                [formGroupName]="idx"
                class="line-item-row d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-2 p-2 mb-2 rounded bg-light border">
                
                <!-- Product SKU -->
                <div class="line-item-col" style="flex: 2;">
                  <label class="modern-field-label d-md-none">{{ "Product SKU" | translate }}</label>
                  <input
                    id="skuInput_{{ idx }}"
                    type="text"
                    class="form-control modern-input form-control-sm text-left line-item-input"
                    [class.is-invalid]="isRowSkuInvalid(item)"
                    placeholder="{{ 'e.g. SKU-1001' | translate }}"
                    formControlName="id"
                    (blur)="onSkuBlur(item)" />
                  <div class="invalid-feedback d-block mt-0.5 text-xs" *ngIf="isRowSkuInvalid(item)">
                    {{ "SKU required" | translate }}
                  </div>
                </div>

                <!-- Product Name -->
                <div class="line-item-col" style="flex: 2;">
                  <label class="modern-field-label d-md-none">{{ "Product Name (optional)" | translate }}</label>
                  <input
                    type="text"
                    class="form-control modern-input form-control-sm text-left line-item-input"
                    placeholder="{{ 'e.g. Wireless Mouse' | translate }}"
                    formControlName="name" />
                </div>

                <!-- Quantity -->
                <div class="line-item-col" style="width: 100%; max-width: 85px;">
                  <label class="modern-field-label d-md-none">{{ "Quantity" | translate }}</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    class="form-control modern-input form-control-sm text-left line-item-input"
                    placeholder="1"
                    formControlName="quantity" />
                </div>

                <!-- Unit Price -->
                <div class="line-item-col" style="width: 100%; max-width: 125px;">
                  <label class="modern-field-label d-md-none">{{ "Unit Price" | translate }}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    class="form-control modern-input form-control-sm text-left line-item-input"
                    placeholder="0.00"
                    formControlName="item_price" />
                </div>

                <!-- Delete Action -->
                <div class="line-item-action d-flex justify-content-end align-items-center" style="width: 36px; height: 34px;">
                  <button
                    type="button"
                    class="btn btn-link text-danger line-item-delete p-0 d-flex align-items-center justify-content-center"
                    (click)="removeContentItem(idx)"
                    [title]="'Remove item' | translate">
                    <i class="fi fi-rr-trash"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- Single Add Line Item Button Row below items -->
            <div class="mt-2">
              <button
                type="button"
                class="btn btn-sm btn-outline-primary"
                (click)="addContentItem()">
                <i class="fi fi-rr-plus mr-1"></i>
                {{ "Add Line Item" | translate }}
              </button>
            </div>
          </div>

          <!-- Total Mismatch Warning -->
          <div class="line-items-mismatch-alert d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-2 p-2.5 mt-3 rounded bg-amber-50 border border-amber-200 text-amber-900" *ngIf="isTotalMismatch">
            <div class="d-flex align-items-center gap-2">
              <i class="fi fi-rr-info text-amber-600 text-base"></i>
              <span class="text-xs font-medium">
                {{ "Line items total" | translate }} {{ form.get('currency')?.value || 'MYR' }} {{ lineItemsTotal.toFixed(2) }}, {{ "but Conversion Amount is" | translate }} {{ form.get('currency')?.value || 'MYR' }} {{ form.get('value')?.value || '0.00' }}.
              </span>
            </div>
            <button
              type="button"
              class="btn btn-sm btn-warning font-semibold text-xs py-1 px-2 whitespace-nowrap"
              (click)="applyLineItemsTotal()">
              {{ "Use line items total" | translate }}
            </button>
          </div>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="modern-modal-footer d-flex justify-content-between align-items-center pt-3">
        <button type="button" class="btn btn-link modern-reset-btn" (click)="resetForm()">
          <i class="fi fi-rr-refresh mr-1"></i>
          {{ "Reset Form" | translate }}
        </button>
        <div class="d-inline-flex align-items-center gap-2">
          <button type="button" class="btn btn-light modern-cancel-btn" (click)="closeModal()">
            {{ "Cancel" | translate }}
          </button>
          <button
            type="submit"
            class="btn modern-submit-btn"
            [ngClass]="mode === 'live' ? 'btn-live-amber' : 'btn-test-blue'"
            [disabled]="isSubmitting">
            <i class="fa fa-spinner fa-spin mr-1" *ngIf="isSubmitting"></i>
            <i class="fi fi-rr-inbox-out mr-1" *ngIf="!isSubmitting"></i>
            <span>{{ (isSubmitting ? "Pushing..." : (mode === 'live' ? "Push Live Event" : "Send Test Event")) | translate }}</span>
          </button>
        </div>
      </div>

    </form>
  </div>
</ng-template>

<!-- Confirmation Modal for LIVE Event Push -->
<ng-template #liveConfirmModal let-modal>
  <div class="modal-header border-bottom-0 pb-0">
    <div class="d-flex align-items-center gap-2">
      <div class="confirm-icon-wrap">
        <i class="fi fi-rr-shield-check text-amber-500"></i>
      </div>
      <div>
        <h5 class="modal-title font-semibold mb-0">{{ "Confirm Live Event Push" | translate }}</h5>
        <small class="text-muted">{{ "Review your transaction details before delivering live to Meta." | translate }}</small>
      </div>
    </div>
    <button type="button" class="close" aria-label="Close" (click)="modal.dismiss('cancel')">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body p-4">
    <div class="confirm-summary-card mb-3">
      <div class="summary-row">
        <span class="summary-label">{{ "Meta Pixel ID" | translate }}</span>
        <strong class="summary-val font-monospace">{{ form.get('pixel_id')?.value }}</strong>
      </div>
      <div class="summary-row">
        <span class="summary-label">{{ "Event Name" | translate }}</span>
        <strong class="summary-val text-primary">{{ getEffectiveEventName() }}</strong>
      </div>
      <div class="summary-row">
        <span class="summary-label">{{ "Sale Location" | translate }}</span>
        <span class="summary-val">
          <span class="badge badge-light border">{{ currentActionSourceObj?.title | translate }} ({{ form.get('action_source')?.value }})</span>
        </span>
      </div>
      <div class="summary-row">
        <span class="summary-label">{{ "Order / Reference No." | translate }}</span>
        <strong class="summary-val font-monospace text-primary">{{ form.get('order_id')?.value }}</strong>
      </div>
      <div class="summary-row">
        <span class="summary-label">{{ "Date & Time" | translate }}</span>
        <span class="summary-val font-semibold">{{ getFormattedEventDateTime() }} ({{ getTimezoneDisplay() }})</span>
      </div>
      <div class="summary-row" *ngIf="form.get('value')?.value">
        <span class="summary-label">{{ "Amount & Currency" | translate }}</span>
        <strong class="summary-val text-success">{{ form.get('currency')?.value }} {{ form.get('value')?.value }}</strong>
      </div>
      <div class="summary-row" *ngIf="form.get('customer_email')?.value || hasCustomerPhone">
        <span class="summary-label">{{ "Customer Identifier" | translate }}</span>
        <span class="summary-val text-muted font-monospace">
          {{ form.get('customer_email')?.value || (form.get('customer_phone_prefix')?.value + ' ' + form.get('customer_phone_number')?.value) }}
        </span>
      </div>
    </div>

    <div class="alert alert-warning p-2.5 rounded text-xs d-flex flex-column gap-1.5 mb-0">
      <div class="d-flex align-items-center gap-2 font-semibold text-amber-900">
        <i class="fi fi-rr-shield-check text-amber-600"></i>
        <span>{{ "Live Production Event" | translate }}</span>
      </div>
      <p class="mb-0 text-amber-800">
        {{ "This event will count towards your ad campaign conversion measurement and ROAS." | translate }}
      </p>
      <div class="pt-1.5 border-top border-amber-200 text-amber-900" style="font-size: 11px;">
        <strong><i class="fi fi-rr-clock-three mr-1"></i>{{ "Meta Reporting Timeframe:" | translate }}</strong>
        {{ "While Meta receives the event immediately, standard dashboard reflection takes 15–30 minutes in Events Manager and up to 24–72 hours in Ads Manager reports." | translate }}
      </div>
    </div>
  </div>

  <div class="modal-footer border-top-0 pt-0 d-flex justify-content-end gap-2">
    <button type="button" class="btn btn-light btn-sm" (click)="modal.dismiss('cancel')">
      {{ "Cancel" | translate }}
    </button>
    <button type="button" class="btn btn-primary btn-sm d-inline-flex align-items-center" (click)="confirmAndPushLive()">
      <i class="fi fi-rr-check mr-1"></i>
      {{ "Confirm & Push" | translate }}
    </button>
  </div>
</ng-template>
